// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/lang/index.lang.tsx
// v1.3.0 - <html lang> folgt IMMER der gewaehlten Sprache (auch beim zweiten Besuch aus dem Speicher) - vorher blieb
//          lang="en" aus index.html stehen: VoiceOver las Deutsch mit englischer Aussprache, Safari bot Uebersetzen an.
// v1.2.0 - Auto-save browser language to 'geo' on first visit
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// Import all language files explicitly
import deTranslations from './de.json';
import enTranslations from './en.json';
import esTranslations from './es.json';
import hrTranslations from './hr.json';
import plTranslations from './pl.json';
import trTranslations from './tr.json';
import ukTranslations from './uk.json';

interface LanguageInfo {
  code: string;
  name: string;
  flag: string;
  name_en: string;
}

interface Translation {
  language: LanguageInfo;
  [key: string]: any;
}

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (languageCode: string) => void;
  t: (path: string) => string;
  languages: Record<string, LanguageInfo>;
  availableLanguages: string[];
  getCountryCode: (languageKey: string) => string;
}

interface LanguageProviderProps {
  children: ReactNode;
}

// Storage key (like Apple's geo cookie)
const STORAGE_KEY = 'geo';

export const translations: Record<string, Translation> = {
  de: deTranslations,
  en: enTranslations,
  es: esTranslations,
  hr: hrTranslations,
  pl: plTranslations,  
  tr: trTranslations,
  uk: ukTranslations
};

export const DEFAULT_LANGUAGE = 'en';

export const LANGUAGES: Record<string, LanguageInfo> = Object.keys(translations).reduce((acc, langKey) => {
  const translation = translations[langKey];
  if (translation.language) {
    acc[langKey] = {
      code: translation.language.code,
      name: translation.language.name,
      flag: translation.language.flag,
      name_en: translation.language.name_en
    };
  }
  return acc;
}, {} as Record<string, LanguageInfo>);

export const isValidLanguageCode = (code: string): boolean => {
  return Object.keys(LANGUAGES).includes(code.toLowerCase());
};

export const getBrowserLanguage = (): string => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  const browserLang = navigator.language.slice(0, 2).toLowerCase();
  return isValidLanguageCode(browserLang) ? browserLang : DEFAULT_LANGUAGE;
};

export const getSavedLanguage = (): string => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  
  // Read from 'geo' (stored as uppercase like Apple: "DE", "EN")
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const langCode = saved.toLowerCase();
    if (isValidLanguageCode(langCode)) {
      return langCode;
    }
  }
  
  // Migration: check old key and migrate
  const oldSaved = localStorage.getItem('estata-language');
  if (oldSaved && isValidLanguageCode(oldSaved)) {
    // Migrate to new format
    localStorage.setItem(STORAGE_KEY, oldSaved.toUpperCase());
    localStorage.removeItem('estata-language');
    return oldSaved.toLowerCase();
  }
  
  return DEFAULT_LANGUAGE;
};

export const getCountryCode = (languageKey: string): string => {
  const translation = translations[languageKey];
  return translation?.language?.code || languageKey.toUpperCase();
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    // Check if already saved
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const langCode = saved.toLowerCase();
      if (isValidLanguageCode(langCode)) {
        return langCode;
      }
    }
    
    // Migration from old key
    const oldSaved = localStorage.getItem('estata-language');
    if (oldSaved && isValidLanguageCode(oldSaved)) {
      localStorage.setItem(STORAGE_KEY, oldSaved.toUpperCase());
      localStorage.removeItem('estata-language');
      return oldSaved.toLowerCase();
    }
    
    // Auto-detect from browser and SAVE it
    const browserLang = getBrowserLanguage();
    localStorage.setItem(STORAGE_KEY, browserLang.toUpperCase());
    document.documentElement.lang = browserLang;
    return browserLang;
  });

  // Die Sprache des Dokuments = die Sprache der Oberflaeche (Screenreader-Aussprache, Uebersetzen-Angebot im Browser)
  useEffect(() => { document.documentElement.lang = currentLanguage; }, [currentLanguage]);

  const changeLanguage = (languageCode: string): void => {
    const normalizedCode = languageCode.toLowerCase();
    if (isValidLanguageCode(normalizedCode)) {
      setCurrentLanguage(normalizedCode);
      // Store as uppercase (like Apple: "DE", "EN", "PL")
      localStorage.setItem(STORAGE_KEY, normalizedCode.toUpperCase());
      document.documentElement.lang = normalizedCode;
    }
  };

  const t = (path: string): string => {
    const keys = path.split('.');
    let value: any = translations[currentLanguage];
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        // Fallback to English
        let fallbackValue: any = translations['en'];
        for (const fallbackKey of keys) {
          if (fallbackValue && typeof fallbackValue === 'object' && fallbackKey in fallbackValue) {
            fallbackValue = fallbackValue[fallbackKey];
          } else {
            return `[${path}]`;
          }
        }
        return fallbackValue;
      }
    }
    
    return typeof value === 'string' ? value : `[${path}]`;
  };

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    t,
    languages: LANGUAGES,
    availableLanguages: Object.keys(LANGUAGES),
    getCountryCode
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
